import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  documentId,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { tenantQuery } from '../../../utils/firestoreHelpers';

// ─────────────────────────────────────────────────────────────
// Saf tarih yardımcıları (Firestore Timestamp ↔ YYYY-MM-DD)
// ─────────────────────────────────────────────────────────────

export function formatDate(date) {
  if (!date || isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatDateToday() {
  return formatDate(new Date());
}

export function parseFirebaseDate(dateStr) {
  if (!dateStr) return null;
  try {
    if (typeof dateStr === 'string') {
      const parts = dateStr.split(' at ');
      const date = new Date(parts[0]);
      if (isNaN(date.getTime())) return null;
      return formatDate(date);
    }
    if (dateStr instanceof Timestamp || typeof dateStr.toDate === 'function') {
      return formatDate(dateStr.toDate());
    }
    return null;
  } catch {
    return null;
  }
}

export function parseFirebaseTime(dateStr) {
  if (!dateStr) return null;
  try {
    if (typeof dateStr === 'string') {
      const parts = dateStr.split(' at ');
      if (parts.length < 2) return null;
      return parts[1].split(' ')[0].substring(0, 5);
    }
    if (dateStr instanceof Timestamp || typeof dateStr.toDate === 'function') {
      const d = dateStr.toDate();
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Subscription: aylık / haftalık randevular
// ─────────────────────────────────────────────────────────────

export function subscribeToAppointments({ businessId, targetDate, viewMode, onData, onError }) {
  const today = new Date();
  let startDate, endDate;

  if (viewMode === 'weekly') {
    const cur = new Date(targetDate);
    const dow = cur.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(cur);
    monday.setDate(cur.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    startDate = monday;
    endDate = sunday;
  } else {
    const monthStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
    monthStart.setHours(0, 0, 0, 0);
    const monthEnd = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);
    startDate = monthStart;
    endDate = monthEnd;

    const isCurrentMonth =
      targetDate.getFullYear() === today.getFullYear() &&
      targetDate.getMonth() === today.getMonth();
    if (isCurrentMonth) {
      const tenDaysLater = new Date(today);
      tenDaysLater.setDate(today.getDate() + 10);
      tenDaysLater.setHours(23, 59, 59, 999);
      if (tenDaysLater > monthEnd) endDate = tenDaysLater;
    }
  }

  const q = tenantQuery(
    collection(db, 'appointments'),
    businessId,
    where('status', 'in', ['pending', 'approved', 'completed', 'canceled']),
    where('date', '>=', Timestamp.fromDate(startDate)),
    where('date', '<=', Timestamp.fromDate(endDate)),
    orderBy('date', 'asc'),
  );

  return onSnapshot(
    q,
    (snap) => {
      const data = snap.docs.map((d) => {
        const appt = d.data();
        return {
          id: d.id,
          ...appt,
          date: parseFirebaseDate(appt.date),
          time: appt.time || parseFirebaseTime(appt.date),
          status: appt.status || 'pending',
          originalCustomerId: appt.customerId,
          originalEmployeeId: appt.employeeId,
          originalServices: appt.services || [],
        };
      });
      onData(data);
    },
    onError,
  );
}

// ─────────────────────────────────────────────────────────────
// Subscription: bekleyen randevular (tarih filtresi yok)
// ─────────────────────────────────────────────────────────────

export function subscribeToPendingAppointments({ businessId, onData, onError }) {
  const q = tenantQuery(
    collection(db, 'appointments'),
    businessId,
    where('status', '==', 'pending'),
    orderBy('date', 'asc'),
  );

  return onSnapshot(
    q,
    (snap) => {
      const data = snap.docs.map((d) => {
        const appt = d.data();
        return {
          id: d.id,
          ...appt,
          date: parseFirebaseDate(appt.date),
          time: appt.time || parseFirebaseTime(appt.date),
        };
      });
      onData(data);
    },
    onError,
  );
}

// ─────────────────────────────────────────────────────────────
// Batch fetch: randevulara ait müşteri / hizmet / çalışan verileri
// ─────────────────────────────────────────────────────────────

function chunkArray(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks;
}

export async function fetchRelatedData(appointmentData) {
  const errors = [];

  const customerIds = [...new Set(appointmentData.map((a) => a.customerId).filter(Boolean))];
  const employeeIds = [...new Set(appointmentData.map((a) => a.employeeId).filter(Boolean))];
  const serviceIds  = [...new Set(appointmentData.flatMap((a) => {
    const ids = [];
    if (a.serviceId) ids.push(a.serviceId);
    (a.services || []).forEach((s) => ids.push(typeof s === 'string' ? s : s.id));
    return ids;
  }).filter(Boolean))];

  const batchFetch = async (collection_, ids, label) => {
    if (!ids.length) return [];
    const results = [];
    try {
      const snaps = await Promise.all(
        chunkArray(ids, 30).map((chunk) =>
          getDocs(query(collection_, where(documentId(), 'in', chunk))),
        ),
      );
      snaps.forEach((snap) =>
        snap.docs.forEach((d) => results.push({ id: d.id, ...d.data() })),
      );
      const found = new Set(results.map((r) => r.id));
      ids.filter((id) => !found.has(id)).forEach((id) =>
        errors.push(`${label} bulunamadı: ${id}`),
      );
    } catch (e) {
      errors.push(`${label} verileri alınamadı: ${e.message}`);
    }
    return results;
  };

  const [customers, services, employees] = await Promise.all([
    batchFetch(collection(db, 'users'),    customerIds, 'Müşteri'),
    batchFetch(collection(db, 'services'), serviceIds,  'Hizmet'),
    batchFetch(collection(db, 'users'),    employeeIds, 'Çalışan'),
  ]);

  return { customers, services, employees, errors };
}

// ─────────────────────────────────────────────────────────────
// Saf enrich: appointment + ilişkili veriler → zenginleştirilmiş appointment
// ─────────────────────────────────────────────────────────────

export function enrichAppointments(appointmentData, customers, services, employees, t) {
  const unknownCustomer = t ? t('fallback.unknownCustomer') : 'Bilinmeyen Müşteri';
  const unknownEmployee = t ? t('fallback.unknownEmployee') : 'Bilinmeyen Çalışan';
  const unknownService  = t ? t('fallback.unknownService')  : 'Bilinmeyen Hizmet';

  return appointmentData.map((appt) => {
    const customer = customers.find((c) => c.id === appt.customerId);
    const employee = employees.find((e) => e.id === appt.employeeId);

    let apptServices = [];
    let totalPrice = 0;

    if (appt.services && Array.isArray(appt.services)) {
      apptServices = appt.services.map((svc) => {
        const id = typeof svc === 'string' ? svc : svc.id;
        const det = services.find((s) => s.id === id);
        const inline = (appt.serviceDetails || []).find((s) => s.id === id);
        const info = {
          id,
          name:     det?.name || inline?.name || unknownService,
          price:    det?.price ?? inline?.price ?? 0,
          sessions: typeof svc === 'object' ? (svc.sessions || 1) : 1,
        };
        totalPrice += Number(info.price || 0);
        return info;
      });
    } else if (appt.serviceId) {
      const det = services.find((s) => s.id === appt.serviceId);
      if (det) {
        apptServices = [{ id: appt.serviceId, name: det.name, price: det.price }];
        totalPrice = Number(det.price || 0);
      }
    }

    return {
      ...appt,
      customerName: customer
        ? `${customer.name} ${customer.surname || ''}`.trim()
        : appt.source === 'guest' && appt.guestName
          ? appt.guestName
          : unknownCustomer,
      employeeName: employee
        ? `${employee.name} ${employee.surname || ''}`.trim()
        : unknownEmployee,
      services:    apptServices,
      serviceName: apptServices.map((s) => s.name).join(', ') || unknownService,
      price:       totalPrice,
    };
  });
}

// ─────────────────────────────────────────────────────────────
// Dropdown verisi (modal açılırken tüm aktif kayıtlar)
// ─────────────────────────────────────────────────────────────

export async function fetchDropdownData(businessId) {
  const [customerSnap, employeeSnap, serviceSnap] = await Promise.all([
    getDocs(tenantQuery(collection(db, 'users'),    businessId, where('role', '==', 'customer'), where('status', '==', 'active'))),
    getDocs(tenantQuery(collection(db, 'users'),    businessId, where('role', '==', 'employee'), where('status', '==', 'active'))),
    getDocs(tenantQuery(collection(db, 'services'), businessId, where('status', '==', 'active'))),
  ]);
  return {
    customers: customerSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    employees: employeeSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    services:  serviceSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };
}

// ─────────────────────────────────────────────────────────────
// CRUD
// ─────────────────────────────────────────────────────────────

export async function saveAppointment({ appointmentData, isEditing, appointmentId, userId, businessId }) {
  const dateTimeStr = `${appointmentData.date}T${appointmentData.time}:00`;
  const appointmentDateTime = new Date(dateTimeStr);

  const groupedServices = appointmentData.services.reduce((acc, svc) => {
    const ex = acc.find((s) => s.id === svc.id);
    if (ex) ex.sessions++;
    else acc.push({ id: svc.id, name: svc.name, price: svc.price, sessions: 1 });
    return acc;
  }, []);

  const payload = {
    customerId:   appointmentData.customerId,
    employeeId:   appointmentData.employeeId,
    services:     groupedServices,
    date:         Timestamp.fromDate(appointmentDateTime),
    status:       appointmentData.status || 'approved',
    notes:        appointmentData.notes || '',
    paymentMethod: appointmentData.paymentMethod || 'Nakit',
    totalPrice:   appointmentData.services.reduce((s, svc) => s + Number(svc.price || 0), 0),
    businessId,
  };

  if (isEditing && appointmentId) {
    await updateDoc(doc(db, 'appointments', appointmentId), {
      ...payload,
      updatedAt: Timestamp.now(),
    });
  } else {
    await addDoc(collection(db, 'appointments'), {
      ...payload,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      userId,
    });
  }
}

export async function softDeleteAppointment(appointmentId) {
  await updateDoc(doc(db, 'appointments', appointmentId), {
    status: 'deleted',
    updatedAt: Timestamp.now(),
  });
}

export async function updateAppointmentStatus(appointmentId, status, extra = {}) {
  await updateDoc(doc(db, 'appointments', appointmentId), {
    status,
    updatedAt: Timestamp.now(),
    ...extra,
  });
}

// Guest randevusu onayı: varsa kullanıcıyı bul, yoksa oluştur
export async function approveGuestAppointment(appointment) {
  let linkedCustomerId = null;

  if (appointment.source === 'guest' && appointment.guestPhone) {
    const snap = await getDocs(
      query(
        collection(db, 'users'),
        where('phone', '==', appointment.guestPhone),
        where('businessId', '==', appointment.businessId),
        limit(1),
      ),
    );

    if (!snap.empty) {
      linkedCustomerId = snap.docs[0].id;
    } else {
      const nameParts = (appointment.guestName || 'Misafir').trim().split(' ');
      const ref = await addDoc(collection(db, 'users'), {
        name:     nameParts[0],
        surname:  nameParts.slice(1).join(' '),
        phone:    appointment.guestPhone,
        role:     'customer',
        businessId: appointment.businessId,
        status:   'active',
        source:   'guest_booking',
        loyalty: {
          currentPoints: 0, lifetimePoints: 0, tier: 'bronze',
          lastCalculatedAt: null, lastExpiryCheckAt: null,
          backfillCompletedAt: null, backfillBillCount: null,
        },
        createdAt: serverTimestamp(),
      });
      linkedCustomerId = ref.id;
    }
  }

  await updateDoc(doc(db, 'appointments', appointment.id), {
    status: 'approved',
    updatedAt: Timestamp.now(),
    ...(linkedCustomerId ? { customerId: linkedCustomerId } : {}),
  });
}
